import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useFormContext } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import { useEffect, useState } from "react";
import { CornerDownRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "./ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Skeleton } from "./ui/skeleton";
import { Label } from "./ui/label";
import { useDataStore } from "@/store/useDataStore";
import { AnimatedNoboriLogoScanner } from "./AnimatedNoboriLogoScanner";

interface Step3 {
  error: string;
  brandId: string;
  isBrandAdd?: boolean;
  clusters: { title: string; clusterId?: string }[];
  isLoading: boolean;
  additionalAction?: () => void;
  loadingStep: boolean;
  company: any;
  registerClusters: (clusters: { title: string; clusterId?: string }[]) => void;
}

export default function StepCluster({
  additionalAction,
  brandId,
  error,
  company,
  clusters,
  isLoading,
  isBrandAdd,
  loadingStep,
  registerClusters,
}: Step3) {
  const { t } = useLocale();
  const {
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
  } = useFormContext();
  const {
    allClusters,
    setAllCluster,
    allSelectedClusters,
    setAllSelectedClusters,
  } = useDataStore();
  const [cluster, setCluster] = useState("");
  const [clusterError, setClusterError] = useState("");
  const [selectedClusters, setSelectedClusters] = useState<number[]>(
    allSelectedClusters.length ? allSelectedClusters : [0, 1],
  );
  const [progress, setProgress] = useState(50);
  const MAX_CLUSTERS_NUMBER = 4;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clusters.length && !isLoading) {
      setSelectedClusters([]);
    }
  }, [clusters]);

  useEffect(() => {
    setValue("clusters", clusters);
    setAllCluster(clusters);
    setAllSelectedClusters(selectedClusters);
    setProgress((selectedClusters.length * 100) / MAX_CLUSTERS_NUMBER);
  }, [clusters, company, selectedClusters]);

  const handleClusterChange = () => {
    if (!cluster.trim().length) {
      return setClusterError("Cluster name can not be empty");
    }

    const currentClusters = allClusters || clusters;

    if (selectedClusters.length >= MAX_CLUSTERS_NUMBER) {
      return setClusterError(
        `You selected all ${MAX_CLUSTERS_NUMBER} clusters.`,
      );
    }

    const allClustersNames = currentClusters.map((p) => p.title);

    if (allClustersNames.includes(cluster)) {
      return setClusterError("Cluster name already exists");
    }

    const newCluster = { title: cluster };
    const newClusters = [...currentClusters, newCluster];
    queryClient.setQueryData(["generateClusters", brandId], newClusters);
    setSelectedClusters([...selectedClusters, newClusters.length - 1]);
    setClusterError("");
    setCluster("");
    clearErrors("clusters");
  };

  const onClustersRegister = () => {
    const { clusters } = getValues();
    const clusterToRegister = clusters.filter(
      (_: { title: string }, index: number) => selectedClusters.includes(index),
    );
    if (!clusterToRegister.length) {
      return setClusterError("Please select at least 1 cluster.");
    }
    setValue("clusters", clusterToRegister);
    registerClusters(clusterToRegister);
  };

  return (
    <div className="flex flex-col">
      <div className="mb-3">
        <div className="flex justify-between font-medium text-sm">
          <div className="flex flex-col w-full">
            {additionalAction && (
              <div>
                <h2 className="text-xl font-semibold text-center mb-2">
                  <LocalizedText id="legacy.select.your.clusters.ewtzhh" />
                </h2>

                <div className="text-center text-[#737373] text-sm mb-6">
                  <LocalizedText id="legacy.choose.the.clusters.you.want.to.track.well.42ioyy" />
                </div>
              </div>
            )}

            {isLoading ? null : (
              <div className="flex justify-between w-full items-center">
                <div className="text-xl font-semibold font-sans">
                  <LocalizedText id="legacy.select.your.clusters.ewtzhh" />
                </div>
                <div className="flex items-center gap-1">
                  <div className="flex items-center text-muted-foreground font-normal">
                    {selectedClusters.length || 0}/{MAX_CLUSTERS_NUMBER}
                  </div>
                  <div className="w-[100px]">
                    <Progress value={progress} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="relative pt-2 flex gap-3">
          <div className="flex flex-col gap-2 opacity-70 flex-grow">
            {Array.from({ length: 4 }).map((_, index) => {
              return (
                <div key={index} className="flex  items-center space-x-4">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-full bg-black/5" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-4 w-full rounded-full bg-black/5" />
                    <Skeleton className="h-4 w-[52%] rounded-full bg-black/5" />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col gap-2 opacity-70 flex-grow">
            {Array.from({ length: 4 }).map((_, index) => {
              return (
                <div key={index} className="flex  items-center space-x-4">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-full bg-black/5" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-4 w-full rounded-full bg-black/5" />
                    <Skeleton className="h-4 w-[52%] rounded-full bg-black/5" />
                  </div>
                </div>
              );
            })}
          </div>
          {additionalAction && (
            <div
              className="absolute inset-0 grid place-items-center pointer-events-none"
              role="status"
              aria-label={t("legacy.generating.suggestions.1jlju1g")}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="grid h-20 w-20 place-items-center rounded-2xl border border-border bg-black shadow-[0_12px_32px_rgba(0,0,0,0.12)]">
                  <AnimatedNoboriLogoScanner
                    size={52}
                    intensity={1.15}
                    durationSec={1.15}
                  />
                </div>
                <div className="text-[#737373] text-sm">
                  <LocalizedText id="legacy.generating.clusters.1jnu9rw" />
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className={`grid grid-cols-2 gap-3 mt-3 ${isBrandAdd && "overflow-y-auto max-h-[188px]"}`}
          >
            {(allClusters || clusters).map((cluster, index) => {
              return (
                <Label
                  className={`flex cursor-pointer items-start gap-2 p-2 border rounded-md ${selectedClusters.includes(index) ? "border-orange-500" : "border-gray-300"}`}
                >
                  <Checkbox
                    checked={selectedClusters.includes(index)}
                    onCheckedChange={() => {
                      if (selectedClusters.includes(index)) {
                        if (selectedClusters.length - 1 === 0) {
                          return setClusterError(
                            `Please keep selected at least 1 cluster.`,
                          );
                        }
                        const filtered = selectedClusters.filter(
                          (idx) => idx !== index,
                        );
                        setClusterError("");
                        setSelectedClusters(filtered);
                      } else {
                        if (selectedClusters.length + 1 > MAX_CLUSTERS_NUMBER) {
                          return setClusterError(
                            `You selected all ${MAX_CLUSTERS_NUMBER} clusters.`,
                          );
                        }
                        setClusterError("");
                        setSelectedClusters([...selectedClusters, index]);
                      }
                    }}
                    className="mt-[2px] cursor-pointer data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500 data-[state=checked]:text-white"
                  />
                  <div className="truncate">
                    <span className={`font-medium text-sm`}>
                      {cluster.title}
                    </span>
                  </div>
                </Label>
              );
            })}
          </div>
          {errors.clusters && (
            <p className="text-red-500 text-sm mt-2">
              {(errors.clusters as FieldError)?.message}
            </p>
          )}
          <div className="mt-4">
            <div className="relative group flex rounded-md">
              <Input
                autoComplete="off"
                placeholder={t("legacy.enter.your.own.cluster.581vym")}
                value={cluster}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!cluster.trim()) return;
                    handleClusterChange();
                  }
                }}
                onChange={(e) => {
                  if (e.target.value.length <= 60) {
                    setClusterError("");
                    setCluster(e.target.value);
                  } else {
                    setClusterError("Cluster name should be max. 60 char.");
                  }
                }}
                className="h-[44px] placeholder:text-[#d5d5d5] focus-visible:placeholder:text-[#737373] items-center bg-white text-sm relative w-full focus-visible:border-[#E5E5E5] pr-[100px] shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 ring-0 outline-none focus:ring-0 mr-1"
              />
              <div className="text-sm absolute right-[60px] bottom-[11px] text-[#d5d5d5] group-focus-within:text-[#737373]">
                {cluster.length}/60
              </div>
              <Button
                type="button"
                className="right-[13px] bottom-[4px] absolute has-[>svg]:px-2 has-[>svg]:py-1 flex cursor-pointer items-center color-[#0A0A0A] rounded-lg"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  handleClusterChange();
                }}
              >
                <CornerDownRight size={16}></CornerDownRight>
              </Button>
            </div>
            {(clusterError || error) && (
              <p className="text-red-500 text-sm mt-2">
                {clusterError || error}
              </p>
            )}
          </div>
          <div className={`flex mt-5 ${additionalAction && "justify-between"}`}>
            {additionalAction ? (
              <Button
                type="button"
                className="cursor-pointer"
                onClick={() => {
                  additionalAction();
                  clearErrors();
                }}
                variant="outline"
              >
                <LocalizedText id="legacy.cancel.18x3nqb" />
              </Button>
            ) : null}
            <div
              className={`${additionalAction ? "" : "w-full"} flex justify-center`}
            >
              <Button
                type="button"
                disabled={loadingStep}
                onClick={onClustersRegister}
                className={`flex cursor-pointer ${additionalAction ? "w-fit" : "w-fit py-[22px] px-[45px]"} hover:bg-[#ff8138] hover:text-white text-white bg-[#FF5E00]`}
              >
                {loadingStep ? (
                  <div className=" mr-2 flex justify-center items-center">
                    <Spinner variant="circle-filled" size={16}></Spinner>
                  </div>
                ) : null}
                <LocalizedText id="legacy.continue.5iqttw" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
